import { useEffect, useState } from 'react';
import {
  CC_STATUS_LABELS,
  type CallCenterOrderDetail,
  type CallCenterOrderEvent,
} from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { useReprintReceipt } from '../print';
import { callCenterApi, ccErrorMessage } from './api';
import { useDriverName } from './driverName';
import { useMoneyFormatter } from './hooks';

/**
 * One order, in full: its lines, its money, and its audit trail.
 *
 * The audit trail is the part that earns its place. Four surfaces move a
 * call-centre order — the agent, the kitchen, the cashier, an admin — and when
 * a customer rings back to ask why their food took fifty minutes, "who had it
 * when" is the entire answer. `cc_order_event` records the source of every
 * transition precisely so this list can show it.
 *
 * `mode` decides which API tier the fetch goes through. An ordinary cashier
 * cannot call the agent endpoints at all, so a shared drawer that always used
 * them would 403 on the counter's own orders.
 */
export function OrderDetailDrawer({
  orderId,
  mode,
  onClose,
  onChanged,
}: {
  orderId: number | null;
  mode: 'agent' | 'branch';
  onClose: () => void;
  onChanged?: (order: CallCenterOrderDetail) => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();
  const reprint = useReprintReceipt();
  const driverName = useDriverName();
  const [detail, setDetail] = useState<CallCenterOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (orderId == null) {
      setDetail(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setCancelReason('');
    // Each tier fetches through its own endpoint. An ordinary cashier holds no
    // call-centre permission, so routing the counter through the agent path
    // would 403 on the branch's own orders and leave this drawer permanently
    // empty for the person who most needs it.
    const fetch = mode === 'agent' ? callCenterApi.agent.get : callCenterApi.branch.get;
    void fetch(orderId)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch(() => {
        if (alive) setDetail(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderId, mode]);

  const driverLabel = driverName(detail);

  if (orderId == null) return null;

  async function handleCancel(): Promise<void> {
    if (!detail) return;
    setCancelling(true);
    try {
      const updated = await callCenterApi.agent.cancel(detail.id, cancelReason.trim() || null);
      setDetail(updated);
      onChanged?.(updated);
      toast(t({ en: 'Order cancelled', ar: 'تم إلغاء الطلب' }), 'success');
    } catch (err) {
      toast(ccErrorMessage(err, 'Failed to cancel'), 'error');
    } finally {
      setCancelling(false);
    }
  }

  const canCancel =
    mode === 'agent' &&
    detail != null &&
    detail.status !== 'delivered' &&
    detail.status !== 'cancelled';

  return (
    <div className="cc-drawer-backdrop" onClick={onClose}>
      <aside className="cc-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="cc-drawer-head">
          <div>
            <div className="cc-drawer-title">
              {t({ en: 'Order', ar: 'الطلب' })} #{detail?.entryNo ?? orderId}
            </div>
            {detail && (
              <div className="cc-drawer-sub">
                {detail.branchName} ·{' '}
                {t(CC_STATUS_LABELS[detail.status])}
              </div>
            )}
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose}>
            ×
          </button>
        </header>

        {loading && <div className="cc-drawer-state">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>}

        {!loading && !detail && (
          <div className="cc-drawer-state">
            {t({ en: 'Could not load this order.', ar: 'تعذّر تحميل هذا الطلب.' })}
          </div>
        )}

        {detail && (
          <div className="cc-drawer-body">
            <section className="cc-drawer-section">
              <h4>{t({ en: 'Customer', ar: 'العميل' })}</h4>
              <div className="cc-kv">
                <span>{t({ en: 'Name', ar: 'الاسم' })}</span>
                <span>
                  {(lang === 'ar' ? detail.customerNameAr || detail.customerName : detail.customerName) ?? '—'}
                </span>
              </div>
              <div className="cc-kv">
                <span>{t({ en: 'Phone', ar: 'الهاتف' })}</span>
                <span>{detail.customerPhone ?? '—'}</span>
              </div>
              {detail.addressSummary && (
                <div className="cc-kv">
                  <span>{t({ en: 'Address', ar: 'العنوان' })}</span>
                  <span>{detail.addressSummary}</span>
                </div>
              )}
              <div className="cc-kv">
                <span>{t({ en: 'Taken by', ar: 'استلمها' })}</span>
                <span>{detail.agentName ?? '—'}</span>
              </div>
              {driverLabel && (
                <div className="cc-kv">
                  <span>{t({ en: 'Driver', ar: 'السائق' })}</span>
                  <span>{driverLabel}</span>
                </div>
              )}
            </section>

            {/*
              Reprint, on the branch tier only. The receipts endpoint is scoped
              to the caller's own subsidiary AND branch, so an agent viewing an
              order they placed for another shop would get a 404 rather than a
              slip — the print belongs where the paper comes out.
            */}
            {mode === 'branch' && (
              <section className="cc-drawer-section cc-drawer-actions">
                <button
                  type="button"
                  className="cc-btn"
                  onClick={() => void reprint(detail.receiptId)}
                >
                  {t({ en: 'Print receipt', ar: 'طباعة الفاتورة' })}
                </button>
              </section>
            )}

            <section className="cc-drawer-section">
              <h4>{t({ en: 'Items', ar: 'الأصناف' })}</h4>
              <table className="cc-lines">
                <tbody>
                  {detail.lines.map((line, i) => (
                    <tr key={`${line.itemId ?? 'x'}-${i}`}>
                      <td className="cc-lines-qty">{line.qty}×</td>
                      <td>
                        {(lang === 'ar' ? line.nameAr || line.name : line.name) ?? '—'}
                        {line.notes && <div className="cc-lines-note">{line.notes}</div>}
                      </td>
                      <td className="cc-lines-amt">{money(line.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cc-kv cc-kv--total">
                <span>{t({ en: 'Total', ar: 'الإجمالي' })}</span>
                <span>{money(detail.totalAmount)}</span>
              </div>
              {detail.deliveryFee > 0 && (
                <div className="cc-kv">
                  <span>{t({ en: 'Delivery', ar: 'التوصيل' })}</span>
                  <span>{money(detail.deliveryFee)}</span>
                </div>
              )}
              <div className="cc-kv">
                <span>{t({ en: 'Payment', ar: 'الدفع' })}</span>
                <span>
                  {detail.paymentTypeLabel ??
                    t({ en: 'Not collected yet', ar: 'لم يتم التحصيل بعد' })}
                </span>
              </div>
            </section>

            {detail.note && (
              <section className="cc-drawer-section">
                <h4>{t({ en: 'Note', ar: 'ملاحظة' })}</h4>
                <p className="cc-drawer-note">{detail.note}</p>
              </section>
            )}

            <section className="cc-drawer-section">
              <h4>{t({ en: 'Timeline', ar: 'المسار الزمني' })}</h4>
              <ol className="cc-timeline">
                {detail.events.map((e) => (
                  <TimelineRow key={e.id} event={e} />
                ))}
              </ol>
            </section>

            {canCancel && (
              <section className="cc-drawer-section">
                <h4>{t({ en: 'Cancel order', ar: 'إلغاء الطلب' })}</h4>
                <input
                  className="cc-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={t({ en: 'Reason', ar: 'السبب' })}
                />
                <button
                  type="button"
                  className="cc-btn cc-btn--danger"
                  disabled={cancelling}
                  onClick={() => void handleCancel()}
                >
                  {cancelling
                    ? t({ en: 'Cancelling…', ar: 'جارٍ الإلغاء…' })
                    : t({ en: 'Cancel this order', ar: 'إلغاء هذا الطلب' })}
                </button>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function TimelineRow({ event }: { event: CallCenterOrderEvent }): JSX.Element {
  const { t } = useLang();
  const label = CC_STATUS_LABELS[event.toStatus];
  const when = new Date(event.createdAt);
  const sourceLabel = t({
    en: {
      call_center: 'Call center',
      kds: 'Kitchen',
      cashier: 'Cashier',
      admin: 'Admin',
      system: 'System',
    }[event.source],
    ar: {
      call_center: 'مركز الاتصال',
      kds: 'المطبخ',
      cashier: 'الكاشير',
      admin: 'الإدارة',
      system: 'النظام',
    }[event.source],
  });

  return (
    <li className={`cc-timeline-row cc-timeline-row--${event.toStatus}`}>
      <span className="cc-timeline-time">
        {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span className="cc-timeline-status">{t(label)}</span>
      <span className="cc-timeline-src">
        {sourceLabel}
        {event.actorName ? ` · ${event.actorName}` : ''}
      </span>
      {event.note && <span className="cc-timeline-note">{event.note}</span>}
    </li>
  );
}
